import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

export default function GlossaryPage({contextPath, mode = "default", height = 575 }) {
    const [allItems, setAllItems] = useState([]);
    const [items, setItems] = useState([]);       // filtered view
    const [keyword, setKeyword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const inputRef = useRef(null);

    const accent = "#e87722";

    // Initial one‑time load
    useEffect(() => {
        const run = async () => {
            setLoading(true); setError("");
            try {
                const headers = {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                    "Content-Type": "application/json",
                };
                const { data } = await axios.get(`${contextPath}/glossary`, { params: { order: "term" }, headers });
                const arr = Array.isArray(data) ? data : [];
                setAllItems(arr);
                setItems(arr);
            } catch (e) {
                setError("Failed to load glossary.");
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [contextPath]);

    // Diacritic‑insensitive normalize helper
    const norm = (s) => String(s || "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "") // strip combining marks
        .toLowerCase();

    // Local filter (no network)
    useEffect(() => {
        const q = norm(keyword.trim());
        if (!q) { setItems(allItems); return; }
        const next = allItems.filter(g => norm(g.term).includes(q));
        setItems(next);
    }, [keyword, allItems]);

    // Keyboard UX: '/' focuses search; Esc clears
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "/") { e.preventDefault(); inputRef.current?.focus(); }
            if (e.key === "Escape") { setKeyword(""); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // Bucketing
    const bucketFor = (term) => {
        const raw = String(term || "").trim();
        const ch = (raw[0] || "").toUpperCase();
        if (/^[A-Z]$/.test(ch)) return ch;
        if (/^[0-9]$/.test(ch)) return "0-9";
        return "#";
    };

    const grouped = useMemo(() => {
        const m = new Map();
        for (const g of items) {
            const b = bucketFor(g.term);
            if (!m.has(b)) m.set(b, []);
            m.get(b).push(g);
        }
        for (const [k, arr] of m) arr.sort((a, b) => a.term?.localeCompare(b.term, undefined, { numeric: true, sensitivity: "base" }));
        return m;
    }, [items]);

    const alpha = useMemo(() => Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)), []);
    const indexOrder = useMemo(() => [...alpha, "0-9"], [alpha]);
    const hasBucket = (b) => (grouped.get(b) || []).length > 0;

    const clearSearch = () => { setKeyword(""); inputRef.current?.focus(); };
    const scrollTo = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };

    return (
        <div className="p-4">
            <h1 className="text-2xl font-semibold">Glossary Listing</h1>

            <div className="mt-4 flex items-center gap-2 flex-wrap">
                <input
                    ref={inputRef}
                    id="keyword"
                    name="keyword"
                    type="text"
                    value={keyword}
                    placeholder="Search term…"
                    onChange={(e) => setKeyword(e.target.value)}
                    className="border rounded px-3 py-1 min-w-[340px] focus:outline-none focus:ring-2 focus:ring-[#465da0] focus:ring-offset-2"
                    style={{ outlineColor: accent }}
                />
                {keyword && (
                    <button type="button" onClick={clearSearch}
                            className="px-4 py-2 bg-[#25408f] text-white rounded-2xl font-semibold shadow-md
                   hover:bg-[#1f367a] active:bg-[#1e3372] active:scale-95 transition-all duration-200
                   focus:outline-none focus:ring-2 focus:ring-[#465da0] focus:ring-offset-2
                   border border-[#25408f] hover:border-[#1e3372] active:border-[#1c306b]
                   disabled:bg-[#25408f] disabled:border-[#25408f] disabled:opacity-60
                   disabled:cursor-not-allowed flex items-center gap-2"
                            disabled={loading}>Clear</button>
                )}
            </div>

            {loading && <p className="mt-2 text-sm text-gray-600">Loading…</p>}
            {error && <p className="mt-2 text-red-600 text-sm">{error}</p>}

            {/* Index incl. 0–9 and # */}
            <div className="mt-4 flex flex-wrap gap-2 text-sm">
                {indexOrder.map((L) => {
                    const enabled = hasBucket(L);
                    return (
                        <button
                            key={L}
                            type="button"
                            onClick={() => enabled && scrollTo(`ref-${L}`)}
                            className={`font-semibold ${enabled ? "hover:underline" : "opacity-30 cursor-default"}`}
                            style={{ color: accent }}
                        >
                            {L}
                        </button>
                    );
                })}
            </div>

            <br/>

            {/* Scroll area */}
            <div className="w-full overflow-auto" style={{ height }}>
                {indexOrder.map((L) => {
                    const arr = grouped.get(L) || [];
                    if (!arr.length) return null;
                    return (
                        <section key={L} id={`ref-${L}`} className="pb-4">
                            <h2 className="text-xl font-semibold flex items-center gap-2">
                                <span style={{ color: accent }}>{L}</span>
                                <span className="flex-1 border-b" />
                            </h2>
                            <ul className="mt-2 space-y-1">
                                {arr.map((g) => (
                                    <li key={g.id} id={`term-${g.id}`}>
                                        <span className="font-semibold" style={{ color: accent }}>{g.term}:</span>
                                        <span className="ml-2">{g.definition}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    );
                })}

                {!loading && items.length === 0 && (
                    <div className="text-sm text-gray-600">No glossary terms found.</div>
                )}
            </div>
        </div>
    );
}
